import { X } from "lucide-react";
import { useEffect, type JSX, type ReactNode } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** Centered modal dialog. Scrim + Esc close. */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 460,
}: ModalProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full rounded-2xl bg-white"
        style={{ maxWidth: width, boxShadow: "var(--sh-modal)" }}
      >
        <div className="flex items-start justify-between gap-3 px-6 pb-1 pt-5">
          <div className="min-w-0">
            {title && <div className="text-[17px] font-extrabold tracking-tight">{title}</div>}
            {subtitle && (
              <div className="mt-0.5 text-[13px] text-[var(--text-secondary)]">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2.5 px-6 pb-5 pt-2">{footer}</div>}
      </div>
    </div>
  );
}
